import { FC, useEffect, useCallback, useState } from 'react';
import intl from 'react-intl-universal';
import { Typography } from '@getsynapse/design-system';
import useModal from 'Hooks/useModal';

interface RemoveMemberConfirmationModalProps {
  onClose: () => void;
  onConfirm: () => void;
}

const RemoveMemberConfirmationModal: FC<RemoveMemberConfirmationModalProps> = ({
  onClose,
  onConfirm,
}) => {
  const { Modal, modalProps, openModal, closeModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!modalProps.isOpen) {
      setIsLoading(false);
      openModal(modalProps);
    }
  }, [modalProps, openModal]);

  const handleClose = useCallback(() => {
    setIsLoading(false);
    closeModal();
    onClose();
  }, [closeModal, onClose]);

  const handleConfirm = useCallback(async () => {
    try {
      setIsLoading(true);
      await onConfirm();
    } catch {
      setIsLoading(false);
      handleClose();
    }
  }, [onConfirm, handleClose]);

  return (
    <Modal
      {...modalProps}
      aria-label={intl.get(
        'ORG_SETTINGS_PAGE.LEARNING_TEAM_DETAILS_PAGE.REMOVE_MEMBER_CONFIRMATION_MODAL.TITLE'
      )}
      title={intl.get(
        'ORG_SETTINGS_PAGE.LEARNING_TEAM_DETAILS_PAGE.REMOVE_MEMBER_CONFIRMATION_MODAL.TITLE'
      )}
      titleIcon={{
        name: 'remove-circle',
        className: 'text-error-dark',
      }}
      size='small'
      closeModal={handleClose}
      actionButtons={[
        {
          children: intl.get('REMOVE'),
          variant: 'primary',
          color: 'danger',
          onClick: handleConfirm,
          loading: isLoading,
        },
        {
          children: intl.get('CANCEL'),
          variant: 'tertiary',
          onClick: handleClose,
        },
      ]}
    >
      <Typography variant='body'>
        {intl.get(
          'ORG_SETTINGS_PAGE.LEARNING_TEAM_DETAILS_PAGE.REMOVE_MEMBER_CONFIRMATION_MODAL.DESCRIPTION'
        )}
      </Typography>
    </Modal>
  );
};

export default RemoveMemberConfirmationModal;
